import React from "react";
// Keep Button if other buttons might be added later, or remove if not.
import { DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import { BeakerIcon } from "@heroicons/react/20/solid";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { NewConsentRecordDto } from "../../../../common/service/server/dto/ConsentRecordDto";
import { usePrimaryOrganizationId } from "../../../hooks";
import { NetworkRequestGate } from "../../../../common/components/NetworkRequestGate";
import { Table, ToolTip, Button, AlertCard } from "@truevault/aquilo";
import { Box, CircularProgress, Typography, TableSortLabel, Tooltip } from "@mui/material";
import Pagination from "@mui/material/Pagination";
import { usePrivacyCenter } from "../../../hooks/usePrivacyCenter";
import { ConsentRecordStats } from "./ConsentRecordStats";
import { ApiRouteHelpers, formatUrl } from "../../../../common/root/apiRoutes";
import {
  ConsentRecordListingProps,
  useConsentRecordListing,
} from "../../../hooks/useConsentRecords";

const ACCEPTED_CIRCLE_ICON = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="10" style={{ fill: "var(--color-green-100)" }} />
    <path
      fillRule="evenodd"
      d="M14.5 7.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 0 1-1.06 0l-2.5-2.5a.75.75 0 0 1 1.06-1.06L9.5 11.44l3.94-3.94a.75.75 0 0 1 1.06 0Z"
      clipRule="evenodd"
      style={{ fill: "var(--color-green-600)" }}
    />
  </svg>
);

const DECLINED_CIRCLE_ICON = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="10" style={{ fill: "var(--color-red-100)" }} />
    <path
      fillRule="evenodd"
      d="M13.07 6.93a.75.75 0 0 1 0 1.06L11.06 10l2.01 2.01a.75.75 0 1 1-1.06 1.06L10 11.06l-2.01 2.01a.75.75 0 0 1-1.06-1.06L8.94 10 6.93 7.99a.75.75 0 0 1 1.06-1.06L10 8.94l2.01-2.01a.75.75 0 0 1 1.06 0Z"
      clipRule="evenodd"
      style={{ fill: "var(--color-red-600)" }}
    />
  </svg>
);

const MIXED_CIRCLE_ICON = (
  <div className="relative" style={{ width: "20px", height: "20px" }}>
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="10" r="10" style={{ fill: "var(--color-yellow-100)" }} />
    </svg>
    <div className="absolute inset-0 flex items-center justify-center">
      <BeakerIcon className="w-3 h-3" style={{ color: "var(--color-yellow-500)" }} />
    </div>
  </div>
);

/**
 * The consent list can be performance-intensive to load. This component wraps the ConsentRecordListing to allow holding
 * the network request until you decide you want to issue it (by rendering this wrapper component).
 */
export const ConsentRecordListingWrapper = () => {
  const consentListing = useConsentRecordListing();

  return (
    <NetworkRequestGate requests={[consentListing.requests.consentRecords]}>
      <ConsentRecordListing {...consentListing.props} />
    </NetworkRequestGate>
  );
};

const shouldShowHistoricalAlert = () => {
  const now = new Date();
  const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
  const june9_2025 = new Date("2025-06-09");

  return sixMonthsAgo < june9_2025;
};

const formatSixMonthsAgoDate = () => {
  const now = new Date();
  const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
  return sixMonthsAgo.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const getTimeSinceJune2024 = () => {
  const startDate = new Date("2025-06-09");
  const now = new Date();

  const diffMs = now.getTime() - startDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

  const format = (value, unit) => rtf.format(value, unit).replace(" ago", "");

  if (diffDays >= 180) return format(-6, "month"); // "6 months"
  if (diffDays >= 30) {
    const months = Math.floor(diffDays / 30);
    return format(-months, "month");
  }
  if (diffDays >= 7) {
    const weeks = Math.floor(diffDays / 7);
    return format(-weeks, "week");
  }
  return format(-diffDays, "day");
};

export const ConsentRecordListing = ({
  loading,
  consentRecords,
  setPageParam,
  setSortParam,
  pageParam,
  sortParam,
}: ConsentRecordListingProps) => {
  const orgId = usePrimaryOrganizationId();
  const [privacyCenter] = usePrivacyCenter(orgId);

  const handleSort = (columnId: string) => {
    const currentSortRule = sortParam.find((rule) => rule.id === columnId);
    if (currentSortRule) {
      // Toggle direction if already sorting by this column
      setSortParam(
        sortParam.map((rule) => (rule.id === columnId ? { ...rule, desc: !rule.desc } : rule)),
      );
    } else {
      // New sort column, default to ascending.
      // This replaces existing sort. If multi-sort is desired, logic needs adjustment.
      // Ensure columnId here matches the actual sortable field names from your backend/DTO
      setSortParam([{ id: columnId, desc: false }]);
    }
  };

  const handleCsvDownload = () => {
    const sortParamString = sortParam
      .map((rule) => `${rule.id}:${rule.desc ? "desc" : "asc"}`)
      .join(",");

    const baseUrl = ApiRouteHelpers.organization.privacyCenter.consentRecordsCsv(
      orgId,
      privacyCenter.id,
    );

    const url = sortParamString ? formatUrl(baseUrl, { sort: sortParamString }) : baseUrl;

    // Create a temporary link element and trigger download
    const link = document.createElement("a");
    link.href = url;
    link.download = `consent-records-${privacyCenter.id}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      key: "visitorId",
      header: (
        <TableSortLabel
          active={sortParam.some((rule) => rule.id === "clientId")}
          direction={sortParam.find((rule) => rule.id === "clientId")?.desc ? "desc" : "asc"}
          onClick={() => handleSort("clientId")}
        >
          Visitor ID
        </TableSortLabel>
      ),
      render: (item: NewConsentRecordDto) => {
        const clientId = item.consentState?.clientId;
        if (!clientId) return "N/A";

        const truncatedId = `${clientId.substring(0, 8)}...`;
        return (
          <ToolTip text={clientId}>
            <span style={{ cursor: "help" }}>{truncatedId}</span>
          </ToolTip>
        );
      },
      className: "px-3 py-3.5 text-left text-sm text-gray-500",
    },
    {
      key: "consentState",
      header: (
        <TableSortLabel
          active={sortParam.some((rule) => rule.id === "consentState")}
          direction={sortParam.find((rule) => rule.id === "consentState")?.desc ? "desc" : "asc"}
          onClick={() => handleSort("consentState")}
        >
          Consents Granted
        </TableSortLabel>
      ),
      render: (item: NewConsentRecordDto) => {
        const formatConsentState = (consentState: any) => {
          if (!consentState || typeof consentState !== "object") {
            return { text: "No consent data", icon: null };
          }

          const consentMapping: Record<string, string> = {
            analyticsPermitted: "Analytics",
            personalizationPermitted: "Personalization",
            adsPermitted: "Ads",
            essentialPermitted: "Essential",
          };

          const grantedPermissions = Object.entries(consentState)
            .filter(([key, value]) => value === true && consentMapping[key])
            .map(([key]) => consentMapping[key]);

          const baseConsentTypes = [
            "analyticsPermitted",
            "personalizationPermitted",
            "adsPermitted",
          ];

          const allGranted = baseConsentTypes.every((type) => consentState[type] === true);
          const allDeclined = baseConsentTypes.every((type) => consentState[type] === false);

          const checkCircleIcon = ACCEPTED_CIRCLE_ICON;
          const xCircleIcon = DECLINED_CIRCLE_ICON;
          const beakerIcon = MIXED_CIRCLE_ICON;

          if (allGranted) {
            return { text: "All Accepted", icon: checkCircleIcon };
          } else if (allDeclined) {
            return { text: "All Declined", icon: xCircleIcon };
          } else if (grantedPermissions.length === 0) {
            return { text: "None", icon: null };
          } else {
            return { text: grantedPermissions.join(", "), icon: beakerIcon };
          }
        };

        const result = formatConsentState(item.consentState);

        return (
          <div className="flex items-center" style={{ fontSize: "0.875rem" }}>
            {result.icon && <span className="mr-2">{result.icon}</span>}
            <span>{result.text}</span>
          </div>
        );
      },
      className: "px-3 py-3.5 text-left text-sm text-gray-500 w-300",
    },
    {
      key: "bannerVariant",
      header: (
        <TableSortLabel
          active={sortParam.some((rule) => rule.id === "bannerVariant")}
          direction={sortParam.find((rule) => rule.id === "bannerVariant")?.desc ? "desc" : "asc"}
          onClick={() => handleSort("bannerVariant")}
        >
          Banner Type
        </TableSortLabel>
      ),
      render: (item: NewConsentRecordDto) => {
        const bannerTypeMapping: Record<string, string> = {
          INFORMATIONAL_ONLY: "Informational",
          INTERACTIVE_DEFAULT_CONSENT: "Opt Out",
          INTERACTIVE_DEFAULT_NO_CONSENT: "Opt In",
        };
        return bannerTypeMapping[item.bannerVariant] || item.bannerVariant;
      },
      className: "px-3 py-3.5 text-left text-sm text-gray-500",
    },
    {
      key: "userCountry",
      header: (
        <TableSortLabel
          active={sortParam.some((rule) => rule.id === "userCountry")}
          direction={sortParam.find((rule) => rule.id === "userCountry")?.desc ? "desc" : "asc"}
          onClick={() => handleSort("userCountry")}
        >
          <div className="flex items-center gap-1">
            Country
            <Tooltip
              title={
                <Typography>
                  Country codes follow the{" "}
                  <a
                    className="tooltip-link underline"
                    href="https://en.wikipedia.org/wiki/ISO_3166-2"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    ISO 3166-2
                  </a>{" "}
                  Standard.
                </Typography>
              }
            >
              <InformationCircleIcon
                className="w-[16px] h-[16px] text-gray-400 cursor-help"
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
          </div>
        </TableSortLabel>
      ),
      className: "px-3 py-3.5 text-left text-sm text-gray-500",
    },
    {
      key: "userRegion",
      header: (
        <TableSortLabel
          active={sortParam.some((rule) => rule.id === "userRegion")}
          direction={sortParam.find((rule) => rule.id === "userRegion")?.desc ? "desc" : "asc"}
          onClick={() => handleSort("userRegion")}
        >
          <div className="flex items-center gap-1">
            Region
            <Tooltip
              title={
                <Typography>
                  Region codes follow the{" "}
                  <a
                    className="tooltip-link underline"
                    href="https://en.wikipedia.org/wiki/ISO_3166-2"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    ISO 3166-2
                  </a>{" "}
                  Standard.
                </Typography>
              }
            >
              <InformationCircleIcon
                className="w-[16px] h-[16px] text-gray-400 cursor-help"
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
          </div>
        </TableSortLabel>
      ),
      className: "px-3 py-3.5 text-left text-sm text-gray-500",
    },
    {
      key: "createdAt",
      header: (
        <TableSortLabel
          active={sortParam.some((rule) => rule.id === "createdAt")}
          direction={sortParam.find((rule) => rule.id === "createdAt")?.desc ? "desc" : "asc"}
          onClick={() => handleSort("createdAt")}
        >
          Date
        </TableSortLabel>
      ),
      render: (item: NewConsentRecordDto) => {
        if (!item.createdAt) return "N/A";
        try {
          const date = new Date(item.createdAt);
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
        } catch (error) {
          return "Invalid Date";
        }
      },
      className: "px-3 py-3.5 text-left text-sm text-gray-500",
    },
  ];

  if (loading && (!consentRecords?.data || consentRecords.data.length === 0)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const csvDownloadButton =
    consentRecords?.data && consentRecords.data.length > 0 ? (
      <Button size="s" color="tertiary" onClick={handleCsvDownload}>
        <DocumentArrowDownIcon className="w-[12px] h-[16px] mr-[8px]" /> Download CSV (Last{" "}
        {getTimeSinceJune2024()})
      </Button>
    ) : null;

  return (
    <>
      <ConsentRecordStats actionButton={csvDownloadButton} />

      <div className="tw-boundary">
        {shouldShowHistoricalAlert() ? (
          <AlertCard
            variant="info"
            title="Historical records available"
            description={
              <>
                Records for the time period from {formatSixMonthsAgoDate()} to June 9, 2025 are
                available upon request by emailing us at:{" "}
                <a href="mailto:help@truevault.com" className="underline">
                  help@truevault.com
                </a>
                .
              </>
            }
          />
        ) : (
          <AlertCard
            variant="info"
            title="Record History"
            description="Consent records are kept for the last 6 months and can be downloaded."
          />
        )}

        <div>
          <div className="mb-2">
            <h2 className="text-lg font-semibold">Consent Records (Last 7 Days)</h2>
          </div>

          {(() => {
            const hasData =
              consentRecords?.data &&
              Array.isArray(consentRecords.data) &&
              consentRecords.data.length > 0;

            if (!hasData && !loading) {
              return <Typography>No cookie consents found.</Typography>;
            } else {
              return (
                <Table
                  data={consentRecords?.data || []}
                  keyExtractor={(item: NewConsentRecordDto) => item.id}
                  columns={columns}
                />
              );
            }
          })()}
        </div>

        {consentRecords?.meta && consentRecords.meta.totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={consentRecords.meta.totalPages}
              page={pageParam}
              onChange={(event, newPage) => setPageParam(newPage)}
              color="primary"
              sx={{
                "& .MuiPaginationItem-root": {
                  margin: "0 4px",
                  minWidth: "32px",
                },
                "& .MuiPaginationItem-ellipsis": {
                  padding: "0 6px",
                },
              }}
            />
          </Box>
        )}
      </div>
    </>
  );
};
